import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ShopError, api, srcSet, type Review, type ShopListItem, type ShopProduct, type ShopVariant } from '../api';
import { discountPercent } from '../money';
import { useMoney } from '../currency';
import { useCart } from '../cart';
import { absolute, useJsonLd, usePageMeta } from '../seo';
import { Stars } from '../components/Stars';
import { ProductReviews } from '../components/ProductReviews';
import { WatchForm } from '../components/WatchForm';
import { ProductRail } from '../components/ProductRail';
import { productToListItem, useRecentlyViewed } from '../recentlyViewed';
import { useT } from '../i18n';
import { isLowStock, isNew } from '../labels';
import { usePromotionLabels } from '../promotionLabels';
import { track } from '../tracking/tracker';
import { UnitPriceNote } from '../storefront/UnitPrice';
import { ShareButtons } from '../storefront/ShareButtons';
import { LayerSlot } from '../layers/LayerSlot';
import { emitProductView } from '../layers/bus';
import { noteProductView } from '../layers/store';

/**
 * MIN/MAX OBJEDNATELNÉ MNOŽSTVÍ (#23) NA DETAILU.
 *
 * Server ta dvě čísla drží na variantě a pokladna (`shop/checkout.ts`) podle
 * nich objednávku ZÁVAZNĚ odmítne. Typ `ShopVariant` v `../api` je zatím
 * nenese (a veřejný katalog `apps/api/src/shop/catalog.ts` je zatím
 * neposílá — oba soubory patří jinam), proto se čtou STRUKTURÁLNĚ: jakmile
 * dorazí, detail je začne respektovat bez další změny; dokud nedorazí, chová
 * se stránka přesně jako dřív a rozhodne stejně server.
 */
interface VariantOrderLimits {
  readonly min: number;
  readonly max: number | null;
}

function orderLimitsOf(variant: ShopVariant | null): VariantOrderLimits {
  const raw = variant as
    | (ShopVariant & { minOrderQuantity?: unknown; maxOrderQuantity?: unknown })
    | null;
  return {
    min:
      typeof raw?.minOrderQuantity === 'number' && Number.isInteger(raw.minOrderQuantity)
        ? Math.max(1, raw.minOrderQuantity)
        : 1,
    max:
      typeof raw?.maxOrderQuantity === 'number' && Number.isInteger(raw.maxOrderQuantity)
        ? raw.maxOrderQuantity
        : null,
  };
}

/** Popis bez znacek, orezany na delku, kterou snese meta znacka. */
function plain(html: string, maxLength = 160): string {
  return html
    .replace(/<[^>]+>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, maxLength);
}

/** Soubor ke stažení u produktu, jak ho posílá veřejné API (bez hashe). */
interface PublicFile {
  readonly id: string;
  readonly title: string;
  readonly contentType: string;
  readonly byteSize: number;
}

/** MIME → krátký štítek typu pro zákazníka. */
const FILE_TYPE_LABEL: Record<string, string> = {
  'application/pdf': 'PDF',
  'image/png': 'PNG',
  'image/jpeg': 'JPG',
  'image/webp': 'WEBP',
  'text/plain': 'TXT',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'DOCX',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'XLSX',
};

function fileTypeLabel(contentType: string): string {
  const known = FILE_TYPE_LABEL[contentType];
  if (known !== undefined) return known;
  // Neznámý typ (nemělo by nastat – server hlídá allowlist): vezmi podtyp.
  const slash = contentType.lastIndexOf('/');
  return (slash === -1 ? contentType : contentType.slice(slash + 1)).toUpperCase().slice(0, 8);
}

/** Velikost souboru v čitelné podobě. Jednotky jsou napříč jazyky stejné. */
function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb < 10 ? kb.toFixed(1) : Math.round(kb)} kB`;
  const mb = kb / 1024;
  return `${mb < 10 ? mb.toFixed(1) : Math.round(mb)} MB`;
}

/**
 * Soubory ke stažení u produktu (#16). Načítají se zvlášť z veřejného API
 * (gated na viditelnost produktu na serveru), aby se detail produktu nemusel
 * měnit. Když produkt žádný soubor nemá nebo se seznam nenačte, sekce se
 * nevykreslí – je to jen doplněk, nesmí shodit stránku. Odkaz míří na server,
 * který soubor pošle jako přílohu (Content-Disposition: attachment) pod
 * popiskem od obsluhy, ne pod hashem.
 */
function ProductFiles({ slug }: { slug: string }) {
  const { t } = useT();
  const [files, setFiles] = useState<readonly PublicFile[]>([]);

  useEffect(() => {
    let live = true;
    setFiles([]);
    fetch(`/api/shop/products/${encodeURIComponent(slug)}/files`, {
      headers: { 'x-requested-with': 'zooshopik-admin' },
    })
      .then((response) => (response.ok ? response.json() : { files: [] }))
      .then((data: { files?: readonly PublicFile[] }) => {
        if (live && Array.isArray(data.files)) setFiles(data.files);
      })
      .catch(() => {
        // Doplněk – když se nenačte, detail produktu funguje dál bez něj.
      });
    return () => {
      live = false;
    };
  }, [slug]);

  if (files.length === 0) return null;

  return (
    <section className="product-files" aria-labelledby="product-files-heading">
      <h2 id="product-files-heading">{t('files.heading')}</h2>
      <ul className="file-list">
        {files.map((file) => (
          <li key={file.id}>
            <a
              className="file-link"
              href={`/api/shop/products/${encodeURIComponent(slug)}/files/${file.id}/download`}
              aria-label={t('files.download', { name: file.title })}
            >
              <span className="file-name">{file.title}</span>
              <span className="file-meta muted small">
                {fileTypeLabel(file.contentType)} · {formatBytes(file.byteSize)}
              </span>
            </a>
          </li>
        ))}
      </ul>
    </section>
  );
}

export function ProductPage() {
  const { format } = useMoney();
  const { t } = useT();
  const { slug = '' } = useParams();
  const navigate = useNavigate();
  const { add } = useCart();

  const [product, setProduct] = useState<ShopProduct | null>(null);
  const [reviews, setReviews] = useState<readonly Review[]>([]);
  const [related, setRelated] = useState<readonly ShopListItem[]>([]);
  const [volumeDiscounts, setVolumeDiscounts] = useState<
    readonly { minQuantity: number; percent: number }[]
  >([]);
  const { items: recentItems, record } = useRecentlyViewed();
  const [selected, setSelected] = useState<ShopVariant | null>(null);
  const [quantity, setQuantity] = useState(1);
  const [priceWatchOpen, setPriceWatchOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Produkt nenalezen jeste neznamena mrtvou adresu – nejdriv se zeptame,
  // jestli se slug jen neprejmenoval. Dokud odpoved nedorazi, hlasku
  // o nenalezeni NEUKAZUJEME, aby neproblikla pred presmerovanim.
  const [checkingRedirect, setCheckingRedirect] = useState(false);
  // Stitky akci – text ze serveru, fail-silent. `''` dokud produkt nedorazi.
  const promoLabels = usePromotionLabels(product?.id ?? '');

  useEffect(() => {
    setLoading(true);
    api
      .getProduct(slug)
      .then((result) => {
        setProduct(result.product);
        setReviews(result.reviews);
        setRelated(result.related);
        setVolumeDiscounts(result.volumeDiscounts);
        // Predvybereme prvni variantu, ktera je skladem.
        const inStock = result.product.variants.find((variant) => variant.available > 0);
        setSelected(inStock ?? result.product.variants[0] ?? null);
        setError(null);
      })
      .catch((caught: unknown) => {
        setError(caught instanceof ShopError ? caught.message : 'Načtení se nezdařilo.');
      })
      .finally(() => setLoading(false));
  }, [slug]);

  // Přepnutí varianty srovná množství do jejího rozsahu (#23). Bez toho by
  // políčko zůstalo na jedničce u balení, které se objednává po šesti, a
  // zákazník by se to dozvěděl až od pokladny.
  useEffect(() => {
    if (selected === null) return;
    const bounds = orderLimitsOf(selected);
    const ceiling =
      bounds.max === null ? selected.available : Math.min(selected.available, bounds.max);
    const floor = Math.min(bounds.min, Math.max(1, ceiling));
    setQuantity((current) => Math.max(floor, Math.min(current, Math.max(1, ceiling))));
  }, [selected]);

  // Zaznam do „naposledy prohlizene" az kdyz je produkt nacteny.
  useEffect(() => {
    if (product !== null) {
      record(productToListItem(product));
      // CDP: hlavní kategorie neexistuje, posílá se pole slugů (max 10).
      track('product_viewed', {
        product_id: product.id,
        slug: product.slug,
        name: product.name,
        price_minor: product.variants[0]?.priceMinor ?? null,
        categories: product.categories.slice(0, 10).map((c) => c.slug),
      });
      // Triggery vrstev (product_views_count) – citac ve store + hranova
      // udalost na sbernici, obojí ZAMERNE mimo track (funguje i bez souhlasu).
      noteProductView();
      emitProductView(product.id);
    }
  }, [product, record]);

  /*
   * Strukturovana data popisuji TOTEZ, co je na strance videt. Rozejit se
   * s ni znamena riskovat penalizaci, ne lepsi umisteni – proto se cena
   * i dostupnost berou z vybrane varianty, ne z nejlevnejsi.
   */
  const jsonLd = useMemo(() => {
    if (product === null || selected === null) return null;

    const productLd = {
      '@context': 'https://schema.org',
      '@type': 'Product',
      name: product.name,
      // Prazdny popis je horsi nez zadny – Google by ho vzal jako
      // prazdnou hodnotu misto toho, aby si ho dohledal ze stranky.
      ...(plain(product.description) === '' ? {} : { description: plain(product.description, 500) }),
      sku: selected.sku,
      ...(product.brand === null ? {} : { brand: { '@type': 'Brand', name: product.brand } }),
      ...(product.images[0] === undefined
        ? {}
        : { image: absolute(product.images[0].url) }),
      ...(product.ratingCount > 0
        ? {
            aggregateRating: {
              '@type': 'AggregateRating',
              ratingValue: product.ratingAverage,
              reviewCount: product.ratingCount,
            },
          }
        : {}),
      offers: {
        '@type': 'Offer',
        price: (selected.priceMinor / 100).toFixed(2),
        priceCurrency: 'CZK',
        availability:
          selected.available > 0
            ? 'https://schema.org/InStock'
            : 'https://schema.org/OutOfStock',
        url: absolute(`/produkt/${product.slug}`),
        // Obchod prodava jen nove zbozi. Bez tohohle pole Merchant u nabidky
        // hlasi varovani a bohaty vysledek se nemusi ukazat.
        itemCondition: 'https://schema.org/NewCondition',
      },
    };

    /*
     * Drobeckova navigace.
     *
     * Kopiruje to, co je na strance videt (viz `<p className="breadcrumb">`
     * niz). Google obojí porovnava a cestu, ktera na strance neni, bud
     * ignoruje, nebo ji bere jako pokus o oklamani. Diky nemu se ve vysledku
     * misto holé adresy ukaze „Zboží > Granule > Losos & rýže".
     */
    const trail = [
      { name: 'Zboží', path: '/' },
      ...product.categories.map((category) => ({
        name: category.name,
        path: `/?kategorie=${category.slug}`,
      })),
      { name: product.name, path: `/produkt/${product.slug}` },
    ];

    const breadcrumbLd = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: trail.map((step, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        name: step.name,
        item: absolute(step.path),
      })),
    };

    return [productLd, breadcrumbLd];
  }, [product, selected]);

  /**
   * PREJMENOVANY PRODUKT – 301 NA NOVOU ADRESU.
   *
   * Presmerovani se musi hledat PRAVE TADY. Adresa `/produkt/stary-slug`
   * matchne routu `/produkt/:slug` driv, nez se dostane k `*`, takze
   * `NotFoundPage` (ktera presmerovani taky umi) se na ni NIKDY nevykresli –
   * a prejmenovani produktu je jediny pripad, na ktery je cely mechanismus
   * postaveny. Skutecny stav 301 umi jen server; tohle je nejvic, co zvladne
   * stranka vykreslena az v prohlizeci, ale odkaz to zachrani hned.
   */
  useEffect(() => {
    if (loading || product !== null || slug === '') return;

    let live = true;
    setCheckingRedirect(true);

    void api
      .redirectFor(`/produkt/${slug}`)
      .then((target) => {
        if (!live) return;
        if (target === null) {
          setCheckingRedirect(false);
          return;
        }
        // `replace`, ne nova polozka v historii: zpetne tlacitko ma vratit
        // tam, odkud navstevnik prisel, ne zpatky na mrtvou adresu.
        void navigate(target, { replace: true });
      })
      .catch(() => {
        // Vypadek dotazu nesmi stranku shodit – zakaznik uvidi hlasku
        // o nenalezeni, coz je porad lepsi nez prazdna obrazovka.
        if (live) setCheckingRedirect(false);
      });

    return () => {
      live = false;
    };
  }, [loading, product, slug, navigate]);

  usePageMeta(
    product === null
      ? {
          // ZAMERNE NE `null`: `usePageMeta(null)` skonci driv, nez nastavi
          // `robots`, takze smazany produkt vrati 200 (nginx posila SPA na
          // kazdou adresu) a zustane BEZ `noindex` – presne ten soft-404,
          // kteremu se chceme vyhnout.
          title: 'Produkt nenalezen | Super Krmivo',
          description: 'Tenhle produkt už v nabídce není.',
          index: false,
        }
      : {
          title: `${product.name} | Super Krmivo`,
          description: plain(product.description, 160),
          imageUrl: product.images[0] === undefined ? null : absolute(product.images[0].url),
          type: 'product',
        },
  );
  useJsonLd(jsonLd);

  if (loading || checkingRedirect) return <p className="muted">Načítám…</p>;
  if (product === null) {
    return (
      <>
        <h1>Produkt nenalezen</h1>
        <p className="muted">{error ?? 'Tenhle produkt už v nabídce není.'}</p>
        <Link className="link" to="/">Zpět do obchodu</Link>
      </>
    );
  }

  const discount =
    selected === null ? null : discountPercent(selected.priceMinor, selected.compareAtPriceMinor);
  const soldOut = selected === null || selected.available <= 0;
  const limits = orderLimitsOf(selected);
  // Strop = minimum ze SKLADU a z LIMITU; co není skladem, se neobjedná ani
  // když to limit dovoluje. Podlaha strop nepřetlačí (viz `clamp` v cart.tsx).
  const maxOrderable =
    selected === null
      ? 1
      : limits.max === null
        ? selected.available
        : Math.min(selected.available, limits.max);
  const minOrderable = Math.min(limits.min, Math.max(1, maxOrderable));
  // Minimum nad skladem znamená, že se varianta TEĎ objednat nedá — přidat ji
  // do košíku by vedlo rovnou k odmítnuté objednávce.
  const belowMinimum = selected !== null && !soldOut && selected.available < limits.min;

  function addToCart() {
    if (product === null || selected === null || soldOut || belowMinimum) return;
    add(
      {
        variantId: selected.id,
        productName: product.name,
        variantName: selected.name,
        slug: product.slug,
        priceMinor: selected.priceMinor,
        available: selected.available,
        // Jen pro ovládání košíku; závazné min/max hlídá server.
        minQuantity: limits.min,
        maxQuantity: limits.max,
      },
      quantity,
    );
    void navigate('/kosik');
  }

  return (
    <>
      <p className="breadcrumb">
        <Link className="link" to="/">Zboží</Link>
        {product.categories.map((category) => (
          <span key={category.slug}>
            {' · '}
            <Link className="link" to={`/?kategorie=${category.slug}`}>{category.name}</Link>
          </span>
        ))}
      </p>

      <div className="detail">
        <div className="thumb large" aria-hidden="true">
          {product.images[0] === undefined ? (
            <span className="thumb-placeholder">{product.name.slice(0, 1)}</span>
          ) : (
            <img
              src={product.images[0].url}
              srcSet={srcSet(product.images[0].thumbnails)}
              /* Na detailu je obrázek přes půl obsahu, na mobilu přes celý. */
              sizes="(max-width: 860px) 100vw, 520px"
              alt={product.images[0].alt}
              decoding="async"
            />
          )}
        </div>

        <div className="detail-body">
          {product.brand !== null && <span className="muted">{product.brand}</span>}
          <h1>{product.name}</h1>

          {(isNew(product.createdAt) ||
            (selected !== null && isLowStock(selected.available)) ||
            promoLabels.length > 0) && (
            <div className="detail-labels">
              {isNew(product.createdAt) && <span className="tag new">Novinka</span>}
              {selected !== null && isLowStock(selected.available) && (
                <span className="tag low">Poslední kusy</span>
              )}
              {/* Akce cilene na tento produkt – text ze serveru. */}
              {promoLabels.map((label) => (
                <span key={label.name} className="tag promo" title={label.name}>
                  {label.badge}
                </span>
              ))}
            </div>
          )}

          {product.ratingCount > 0 && (
            <a href="#hodnoceni" className="rating-link">
              <Stars value={product.ratingAverage} count={product.ratingCount} />
            </a>
          )}

          {product.variants.length > 1 && (
            <div className="field">
              <span>Varianta</span>
              <div className="chips">
                {product.variants.map((variant) => (
                  <button
                    key={variant.id}
                    type="button"
                    className={selected?.id === variant.id ? 'chip active' : 'chip'}
                    onClick={() => {
                      setSelected(variant);
                      setQuantity(1);
                    }}
                    disabled={variant.available <= 0}
                  >
                    {variant.name}
                    {variant.available <= 0 && ' — vyprodáno'}
                  </button>
                ))}
              </div>
            </div>
          )}

          {selected !== null && (
            <div className="buy-card">
              <p className="price large">
                {format(selected.priceMinor)}
                {selected.compareAtPriceMinor !== null && (
                  <s className="muted"> {format(selected.compareAtPriceMinor)}</s>
                )}
                {discount !== null && <span className="tag sale inline">−{discount} %</span>}
              </p>
              {/* Cena za měrnou jednotku (#9) — hned u ceny, protože jinak
                  není s čím ji srovnávat. Když ji server neposlal (obsah
                  balení se z názvu spolehlivě přečíst nedá), není tu nic. */}
              <UnitPriceNote source={selected} />
              <p className="muted small">Cena včetně {product.vatRate} % DPH.</p>

              {volumeDiscounts.length > 0 && (
                <table className="volume-table">
                  <caption>Kup víc a ušetři</caption>
                  <tbody>
                    {volumeDiscounts.map((tier) => (
                      <tr key={tier.minQuantity}>
                        <td>od {tier.minQuantity} ks</td>
                        <td>−{tier.percent} %</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}

              <p className={selected.available > 0 ? 'stock in' : 'stock out'}>
                {selected.available > 0
                  ? `Skladem ${selected.available} ks`
                  : 'Momentálně vyprodáno'}
              </p>

              {/* Min/max objednatelné množství (#23). Věta se ukazuje jen
                  když nějaké omezení je – drtivá většina katalogu žádné nemá. */}
              {!soldOut && (limits.min > 1 || limits.max !== null) && (
                <p className={belowMinimum ? 'alert' : 'muted small'}>
                  {belowMinimum
                    ? t('qty.belowStock', { min: limits.min })
                    : limits.max === null
                      ? t('qty.minInfo', { min: limits.min })
                      : limits.min > 1
                        ? t('qty.rangeInfo', { min: limits.min, max: limits.max })
                        : t('qty.maxInfo', { max: limits.max })}
                </p>
              )}

              {!soldOut && (
                <div className="buy">
                  <label className="field quantity">
                    <span>Počet</span>
                    <input
                      type="number"
                      min={minOrderable}
                      max={maxOrderable}
                      step={1}
                      value={quantity}
                      disabled={belowMinimum}
                      onChange={(event) => {
                        const next = Number(event.target.value);
                        setQuantity(
                          Number.isInteger(next) && next > 0
                            ? Math.max(minOrderable, Math.min(next, maxOrderable))
                            : minOrderable,
                        );
                      }}
                    />
                  </label>
                  <button
                    type="button"
                    className="button primary"
                    onClick={addToCart}
                    disabled={belowMinimum}
                  >
                    Do košíku
                  </button>
                </div>
              )}

              {soldOut && (
                <WatchForm key={selected.id} slug={product.slug} variant={selected} />
              )}

              {!soldOut && (
                <div className="price-watch-cta">
                  {priceWatchOpen ? (
                    <WatchForm
                      key={`price-${selected.id}`}
                      kind="price"
                      slug={product.slug}
                      variant={selected}
                    />
                  ) : (
                    <button
                      type="button"
                      className="price-watch-toggle"
                      onClick={() => setPriceWatchOpen(true)}
                    >
                      Hlídat cenu — dáme vědět, až zlevní
                    </button>
                  )}
                </div>
              )}

              <ul className="detail-trust" aria-label="Naše výhody">
                <li className="trust-ship">Skladem — expedujeme ještě dnes</li>
                <li className="trust-free">Doprava zdarma nad 1 500 Kč</li>
                <li className="trust-return">Vrácení do 30 dnů bez udání důvodu</li>
              </ul>
            </div>
          )}

          {product.description !== '' && <p className="description">{product.description}</p>}

          {/* Sdílení (#31). Obyčejné odkazy — žádný cizí skript, žádné
              sledování. Sdílí se kanonická adresa, ne ta z prohlížeče. */}
          <ShareButtons title={product.name} />
        </div>
      </div>

      <ProductFiles slug={product.slug} />

      <LayerSlot name="product_below_description" />

      <ProductRail title="Mohlo by se hodit" items={related} />

      <div id="hodnoceni">
        <ProductReviews
          slug={product.slug}
          average={product.ratingAverage}
          count={product.ratingCount}
          reviews={reviews}
        />
      </div>

      <ProductRail
        title="Naposledy prohlížené"
        items={recentItems.filter((item) => item.id !== product.id)}
      />
    </>
  );
}
